import type { ITenantDestination } from '@/shared/api/types/tenant-destination-types'
import type { ITour, ITourPoi, TourBasicInfoState } from '@/shared/api/types/tour-types'
import { DEFAULT_TOUR_BASIC_INFO } from '@/shared/constants/tour-form'

export function sortTourPoisByOrder(pois: ITourPoi[]): ITourPoi[] {
  return pois.slice().sort((a, b) => a.order - b.order)
}

/**
 * VI: Khi mở chế độ edit, ưu tiên tenant_destination_id của chính tour; chỉ suy ra từ POI khi backend chưa trả field này. Nếu mọi POI cùng một destination thì dùng destination đó, ngược lại để trống (scope theo tỉnh) để tránh gán sai.
 * EN: In edit mode prefer the tour's own tenant_destination_id; only infer it from the POIs when the backend omits it. If all POIs share one destination use it, otherwise leave it empty (province scope) to avoid a wrong assignment.
 */
export function resolveTourTenantDestinationId(tour: ITour, tourPois: ITourPoi[]): string {
  if (tour.tenantDestinationId) {
    return tour.tenantDestinationId
  }
  const destinationIds = new Set(
    tourPois.map((item) => item.poi?.tenantDestinationId).filter((id): id is string => Boolean(id)),
  )
  const [onlyDestinationId] = destinationIds
  return destinationIds.size === 1 && onlyDestinationId ? onlyDestinationId : ''
}

/** Basic-info values of an existing tour. Country/province are filled in later from the destination. */
export function deriveBasicInfoFromTour(tour: ITour, tourPois: ITourPoi[]): TourBasicInfoState {
  const tenantDestinationId = resolveTourTenantDestinationId(tour, tourPois)
  return {
    duration: tour.duration ?? DEFAULT_TOUR_BASIC_INFO.duration,
    scopeType: tenantDestinationId ? 'tenantDestination' : 'province',
    countryId: '',
    provinceId: '',
    tenantDestinationId,
    ticketLink: tour.ticketLink ?? '',
    isFavorite: tour.isFavorite ?? false,
  }
}

export interface TourGeoScope {
  countryId: string
  provinceId: string
}

/** Country/province of the chosen destination, or `null` while the destination is not loaded yet. */
export function findDestinationGeo(
  destinationId: string,
  destinations: ITenantDestination[],
): TourGeoScope | null {
  const destination = destinations.find((item) => item.id === destinationId)
  if (!destination) {
    return null
  }
  return { countryId: destination.countryId ?? '', provinceId: destination.provinceId ?? '' }
}

/**
 * VI: Khi chưa có tỉnh nhưng đã chọn POI, suy ra country/province chung của các POI để admin đỡ phải chọn tay. Chỉ trả kết quả khi MỌI POI thuộc destination đã tải và đều chung một tỉnh + một quốc gia.
 * EN: With no province yet but POIs selected, infer the common country/province to spare manual input. Returns a result only when EVERY POI belongs to a loaded destination and they all share one province and one country.
 */
export function inferGeoFromSelectedPois(
  selectedPois: ITourPoi[],
  destinations: ITenantDestination[],
): TourGeoScope | null {
  const destinationIds = Array.from(
    new Set(
      selectedPois
        .map((item) => item.poi?.tenantDestinationId)
        .filter((id): id is string => Boolean(id)),
    ),
  )
  if (destinationIds.length === 0) {
    return null
  }
  const matched = destinationIds
    .map((id) => destinations.find((item) => item.id === id))
    .filter((item): item is ITenantDestination => Boolean(item))
  if (matched.length !== destinationIds.length) {
    return null
  }
  const provinceIds = new Set(matched.map((item) => item.provinceId))
  const countryIds = new Set(matched.map((item) => item.countryId))
  const [provinceId] = provinceIds
  const [countryId] = countryIds
  if (provinceIds.size !== 1 || countryIds.size !== 1) {
    return null
  }
  return { countryId: countryId ?? '', provinceId: provinceId ?? '' }
}

/**
 * VI: Khi user đổi điểm đến, country/province được ghi đè theo điểm đến mới để bộ lọc địa lý luôn khớp; giá trị cũ chỉ được giữ khi điểm đến thiếu dữ liệu.
 * EN: When the user picks another destination, country/province are overwritten from it so the geographic filters stay consistent; the previous values are kept only if the destination has none.
 */
export function syncBasicInfoWithDestination(
  value: TourBasicInfoState,
  destinations: ITenantDestination[],
): TourBasicInfoState {
  const destination = destinations.find((item) => item.id === value.tenantDestinationId)
  if (!value.tenantDestinationId || !destination) {
    return value
  }
  return {
    ...value,
    countryId: destination.countryId ?? value.countryId,
    provinceId: destination.provinceId ?? value.provinceId,
  }
}
